import React, { useState } from 'react';
import './App.css';
// Import your actual package
import { ProtectedText } from 'react-text-protect';

export default function App() {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="app">
      <section className="hero">
        <div className="hero-content">
          <div className="hero-left">
            <h1 className="main-title">react-text-protect</h1>
            <p className="subtitle">
              A React component that protects content from casual copying with clipboard 
              encryption, watermarking, and DevTools detection. Perfect for protecting 
              educational content and assessments.
            </p>
            
            <div className="glass-card install-card">
              <div className="card-label">Installation</div>
              <div className="code-block">
                <code>npm install react-text-protect</code>
              </div>
            </div>

            <div className="features">
              <div className="feature-item">✓ Clipboard encryption</div>
              <div className="feature-item">✓ Screenshot watermarking</div>
              <div className="feature-item">✓ DevTools detection</div>
            </div>
          </div>

          <div className="hero-right">
            <div className="glass-card code-demo">
              <div className="card-label">Usage Example</div>
              <pre className="code-block-large">
                <span className="code-import">import</span> ProtectedText <span className="code-import">from</span> <span className="code-string">"react-text-protect"</span>;
                {'\n\n'}
                <span className="code-function">function</span> <span className="code-function-name">App</span>() {'{'}
                {'\n  '}
                <span className="code-return">return</span> (
                {'\n    '}
                {'<'}<span className="code-component">ProtectedText</span> 
                {'\n      '}<span className="code-prop">userId</span>=<span className="code-string">"student_123"</span>
                {'\n      '}<span className="code-prop">showWatermark</span>={'{true}'}
                {'\n    '}
                {'>'}
                {'\n      '}Protected content here
                {'\n    '}
                {'</'}<span className="code-component">ProtectedText</span>{'>'}
                {'\n  '}
                );
                {'\n'}
                {'}'}
              </pre>
            </div>
          </div>
        </div>
        <div className="scroll-indicator">
          <div className="scroll-arrow">↓</div>
        </div>
      </section>

      <section className="details">
        <div className="details-content">
          <div className="glass-card">
            <div className="card-label">Live Demo - Try Copying This Text</div>
            <p className="demo-description">
              This text is protected with all features enabled. Try copying it:
            </p>
            
            <div className="demo-box">
              <ProtectedText userId="DEMO_USER" showWatermark={true}>
                This is a live example of protected text with watermarking and encryption. 
                Try copying this sentence and pasting it into a text editor. You'll see 
                encrypted gibberish instead of the original text! Also notice the watermark 
                overlay if you take a screenshot.
              </ProtectedText>
            </div>

            <div className="demo-instructions">
              <div className="instruction-step">1. Select the text above</div>
              <div className="instruction-step">2. Copy it (Ctrl+C / Cmd+C)</div>
              <div className="instruction-step">3. Paste to see encrypted output</div>
            </div>
          </div>

          <div className="glass-card">
            <div className="card-label">Feature 1: Clipboard Encryption</div>
            <p className="demo-description">
              When you copy the protected text above, you'll get something like this:
            </p>
            <div className="encrypted-output" onClick={handleCopy}>
              Hvwg wg o fwjs sboaczs ct ezchsqhsr hslh jwhv johsfaofxwbu ofs sbqfmdhwcb. 
              Hfm qcdmwbu hvwg gsahsaqs ofs doghwbu wh wbhc o hslh stwhcf. Mci'zz gss 
              sbqfmdhsr uwppsfwgv wbghsot ct hvs cfwuwboz hslh!
            </div>
            {copied && <div className="copy-feedback">✓ Copied encrypted text!</div>}
            <div className="info-note">
              <span className="note-icon">🔒</span>
              The encryption key is randomly generated and never exposed.
            </div>
          </div>

          <div className="glass-card">
            <div className="card-label">Feature 2: Screenshot Watermarking</div>
            <p className="demo-description">
              Notice the semi-transparent watermark in the protected text above. It shows:
            </p>
            
            <div className="watermark-info">
              <div className="watermark-detail">
                <span className="detail-icon">👤</span>
                <span className="detail-text">User ID (e.g., "DEMO_USER")</span>
              </div>
              <div className="watermark-detail">
                <span className="detail-icon">🕐</span>
                <span className="detail-text">Timestamp (current date & time)</span>
              </div>
            </div>
            
            <div className="info-note">
              <span className="note-icon">📸</span>
              Watermarks make screenshots traceable even if captured via phone camera.
            </div>
          </div>

          <div className="glass-card">
            <div className="card-label">Feature 3: DevTools Detection</div>
            <p className="demo-description">
              Try opening your browser's Developer Tools (F12 or Cmd+Option+I). 
              The protected content will automatically hide and show a warning message.
            </p>
            
            <div className="devtools-demo">
              <div className="devtools-preview">
                <div className="devtools-warning">
                  ⚠️ Developer Tools Detected - Content Hidden
                </div>
              </div>
              <p className="devtools-note">This is what users see when DevTools are open</p>
            </div>
          </div>

          <div className="glass-card">
            <div className="card-label">API Reference</div>
            <div className="api-section">
              <pre className="code-block-large">
                {'<'}<span className="code-component">ProtectedText</span>{' '}
                {'\n  '}<span className="code-prop">userId</span>=<span className="code-string">"user_id"</span>        <span className="code-comment">// Default: "USER"</span>
                {'\n  '}<span className="code-prop">showWatermark</span>={'{true}'}    <span className="code-comment">// Default: true</span>
                {'\n'}
                {'>'}
                {'\n  '}Your protected content here
                {'\n'}
                {'</'}<span className="code-component">ProtectedText</span>{'>'}
              </pre>
            </div>
          </div>

          <div className="glass-card">
            <div className="card-label">Use Cases</div>
            <div className="use-cases">
              <div className="use-case">
                <div className="use-case-title">Educational Platforms</div>
                <div className="use-case-desc">Prevent students from copying exam questions during timed tests</div>
              </div>
              <div className="use-case">
                <div className="use-case-title">Proprietary Content</div>
                <div className="use-case-desc">Add friction to copying confidential documents</div>
              </div>
              <div className="use-case">
                <div className="use-case-title">Content Traceability</div>
                <div className="use-case-desc">Watermark screenshots to identify source of leaks</div>
              </div>
            </div>
          </div>

          <div className="glass-card">
            <div className="card-label">Honest Limitations</div>
            <div className="limitations">
              <div className="limitation-item">❌ Can be bypassed by disabling JavaScript</div>
              <div className="limitation-item">❌ DevTools detection can be fooled</div>
              <div className="limitation-item">❌ Phone cameras bypass all protections</div>
              <div className="limitation-item">✅ Good for timed exams where effort matters</div>
              <div className="limitation-item">✅ Adds friction to casual copying and sharing</div>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <p>Built for developers who value content security</p>
        <a href="https://github.com/KabirG7/React-Text-Protect" target="_blank" rel="noopener noreferrer" className="github-link">
          View on GitHub →
        </a>
      </footer>
    </div>
  );
}